import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  ArrowRight,
  Camera,
  CreditCard,
  Mail,
  MapPin,
  MessageSquare,
  Search,
  Star,
} from 'lucide-react';
import Zoom from 'react-medium-image-zoom';
import '../../styles/reactMediumImageZoom.css';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useIntl } from '../../util/reactIntl';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import aboutStoryCreatives from '../../assets/about/about-story-creatives.png';
import lineQr from '../../assets/about/line-qr.png';
import teamIulia from '../../assets/about/team-iulia.avif';
import teamJannik from '../../assets/about/team-jannik.png';
import teamManit from '../../assets/about/team-manit.jpg';
import teamMin from '../../assets/about/team-min.jpg';
import teamTristan from '../../assets/about/team-tristan.png';
import teamVishal from '../../assets/about/team-vishal.png';
import lineLogo from '../../assets/line.png';

import css from './AboutPage.module.css';

const lineOfficialAccountUrl = 'https://lin.ee/ZFujHik';

const brandStoryImages = {
  horizontal: aboutStoryCreatives,
  vertical: null,
};

const lineQrImage = lineQr;

const team = [
  {
    nameId: 'AboutPage.teamManitName',
    roleId: 'AboutPage.teamManitRole',
    image: teamManit,
    initials: 'M',
  },
  {
    nameId: 'AboutPage.teamJannikName',
    roleId: 'AboutPage.teamJannikRole',
    image: teamJannik,
    initials: 'J',
  },
  {
    nameId: 'AboutPage.teamTristanName',
    roleId: 'AboutPage.teamTristanRole',
    image: teamTristan,
    initials: 'T',
  },
  {
    nameId: 'AboutPage.teamVishalName',
    roleId: 'AboutPage.teamVishalRole',
    image: teamVishal,
    initials: 'V',
  },
  {
    nameId: 'AboutPage.teamIuliaName',
    roleId: 'AboutPage.teamIuliaRole',
    image: teamIulia,
    initials: 'I',
  },
  {
    nameId: 'AboutPage.teamMinName',
    roleId: 'AboutPage.teamMinRole',
    image: teamMin,
    initials: 'M',
  },
];

const stats = [
  {
    valueId: 'AboutPage.statScoutingCostValue',
    labelId: 'AboutPage.statScoutingCostLabel',
  },
  {
    valueId: 'AboutPage.statDmTimeValue',
    labelId: 'AboutPage.statDmTimeLabel',
  },
  {
    valueId: 'AboutPage.statOpaqueValue',
    labelId: 'AboutPage.statOpaqueLabel',
  },
];

const audiences = [
  {
    titleId: 'AboutPage.audienceHostsTitle',
    descriptionId: 'AboutPage.audienceHostsDescription',
  },
  {
    titleId: 'AboutPage.audienceCreativesTitle',
    descriptionId: 'AboutPage.audienceCreativesDescription',
  },
  {
    titleId: 'AboutPage.audienceOperatorsTitle',
    descriptionId: 'AboutPage.audienceOperatorsDescription',
  },
];

const steps = [
  {
    titleId: 'AboutPage.stepDiscoverTitle',
    descriptionId: 'AboutPage.stepDiscoverDescription',
    icon: Search,
  },
  {
    titleId: 'AboutPage.stepRequestTitle',
    descriptionId: 'AboutPage.stepRequestDescription',
    icon: MessageSquare,
  },
  {
    titleId: 'AboutPage.stepPayTitle',
    descriptionId: 'AboutPage.stepPayDescription',
    icon: CreditCard,
  },
  {
    titleId: 'AboutPage.stepShootTitle',
    descriptionId: 'AboutPage.stepShootDescription',
    icon: Camera,
  },
  {
    titleId: 'AboutPage.stepReviewTitle',
    descriptionId: 'AboutPage.stepReviewDescription',
    icon: Star,
  },
];

const Schema = (name, description) => ({
  '@context': 'http://schema.org',
  '@type': 'AboutPage',
  name,
  description,
});

const SectionLabel = props => <p className={css.sectionLabel}>{props.children}</p>;

const ImagePlaceholder = props => (
  <div className={props.className} role="img" aria-label={props.label}>
    <span>{props.text}</span>
  </div>
);

const AboutPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const t = id => intl.formatMessage({ id });
  const pageTitle = t('AboutPage.pageTitle');
  const pageDescription = t('AboutPage.pageDescription');

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      schema={Schema(pageTitle, pageDescription)}
      scrollingDisabled={scrollingDisabled}
      hasLocalizedVariants
    >
      <LayoutSingleColumn
        className={css.layoutRoot}
        topbar={<TopbarContainer currentPage="AboutPage" />}
        footer={<FooterContainer />}
      >
        <article className={css.root}>
          <header className={css.hero}>
            <div className={css.container}>
              <SectionLabel>{t('AboutPage.heroLabel')}</SectionLabel>
              <h1 className={css.heroTitle}>{t('AboutPage.heroTitle')}</h1>
              <p className={css.heroDescription}>{pageDescription}</p>
            </div>
          </header>

          <section className={`${css.section} ${css.storySection}`}>
            <div className={css.container}>
              <div className={css.storyGrid}>
                <div className={css.storyMedia}>
                  {brandStoryImages.horizontal ? (
                    <img
                      className={css.storyImage}
                      src={brandStoryImages.horizontal}
                      alt={t('AboutPage.storyImageAlt')}
                      loading="lazy"
                    />
                  ) : (
                    <ImagePlaceholder
                      className={css.storyImagePlaceholder}
                      label={t('AboutPage.storyImagePlaceholderLabel')}
                      text={t('AboutPage.storyImagePlaceholderText')}
                    />
                  )}
                  {brandStoryImages.vertical ? (
                    <img
                      className={css.storyImageInset}
                      src={brandStoryImages.vertical}
                      alt={t('AboutPage.storyImageInsetAlt')}
                      loading="lazy"
                    />
                  ) : null}
                </div>

                <div className={css.storyContent}>
                  <SectionLabel>{t('AboutPage.storyLabel')}</SectionLabel>
                  <h2 className={css.sectionTitle}>{t('AboutPage.storyTitle')}</h2>
                  <div className={css.richText}>
                    <p>{t('AboutPage.storyParagraph1')}</p>
                    <p>{t('AboutPage.storyParagraph2')}</p>
                    <p>{t('AboutPage.storyParagraph3')}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.container}>
              <div className={css.problemIntro}>
                <SectionLabel>{t('AboutPage.problemLabel')}</SectionLabel>
                <h2 className={css.sectionTitle}>{t('AboutPage.problemTitle')}</h2>
                <p className={css.sectionDescription}>{t('AboutPage.problemDescription')}</p>
              </div>
              <div className={css.statsGrid}>
                {stats.map(stat => (
                  <div className={css.statCard} key={stat.valueId}>
                    <strong>{t(stat.valueId)}</strong>
                    <p>{t(stat.labelId)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={css.highlightSection}>
            <div className={css.container}>
              <SectionLabel>{t('AboutPage.highlightLabel')}</SectionLabel>
              <p className={css.highlightText}>{t('AboutPage.highlightText')}</p>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.container}>
              <div className={css.audienceIntro}>
                <SectionLabel>{t('AboutPage.audienceLabel')}</SectionLabel>
                <h2 className={css.sectionTitle}>{t('AboutPage.audienceTitle')}</h2>
                <p className={css.sectionDescription}>{t('AboutPage.audienceDescription')}</p>
              </div>
              <div className={css.audienceGrid}>
                {audiences.map(audience => (
                  <div className={css.audienceCard} key={audience.titleId}>
                    <h3>{t(audience.titleId)}</h3>
                    <p>{t(audience.descriptionId)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={`${css.section} ${css.bookingStepsSection}`}>
            <div className={css.container}>
              <div className={css.bookingStepsIntro}>
                <SectionLabel>{t('AboutPage.bookingStepsLabel')}</SectionLabel>
                <h2 className={css.sectionTitle}>{t('AboutPage.bookingStepsTitle')}</h2>
              </div>
              <div className={css.stepsGrid}>
                {steps.map((step, index) => {
                  const StepIcon = step.icon;
                  return (
                    <div className={css.stepCard} key={step.titleId}>
                      <div className={css.stepHeader}>
                        <div className={css.stepIconWrap}>
                          <StepIcon aria-hidden="true" size={17} strokeWidth={2.2} />
                        </div>
                        <span className={css.stepNumber}>{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h3>{t(step.titleId)}</h3>
                      <p>{t(step.descriptionId)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className={css.quoteSection}>
            <div className={css.container}>
              <blockquote className={css.quote}>
                <p>{t('AboutPage.quoteText')}</p>
                <footer>{t('AboutPage.quoteFooter')}</footer>
              </blockquote>
            </div>
          </section>

          <section className={`${css.section} ${css.teamSection}`}>
            <div className={css.container}>
              <div className={css.teamIntro}>
                <SectionLabel>{t('AboutPage.teamLabel')}</SectionLabel>
                <h2 className={css.sectionTitle}>{t('AboutPage.teamTitle')}</h2>
              </div>
              <div className={css.teamGrid}>
                {team.map(member => {
                  const memberName = t(member.nameId);
                  const memberRole = t(member.roleId);
                  return (
                    <div className={css.teamCard} key={member.nameId}>
                      {member.image ? (
                        <img
                          className={css.teamImage}
                          src={member.image}
                          alt={intl.formatMessage(
                            { id: 'AboutPage.teamMemberImageAlt' },
                            { name: memberName, role: memberRole }
                          )}
                          loading="lazy"
                        />
                      ) : (
                        <div className={css.teamAvatar} aria-hidden="true">
                          {member.initials}
                        </div>
                      )}
                      <div className={css.teamCopy}>
                        <h3>{memberName}</h3>
                        <p>{memberRole}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className={css.contactSection}>
            <div className={css.container}>
              <div className={css.contactGrid}>
                <div className={css.contactIntro}>
                  <SectionLabel>{t('AboutPage.contactLabel')}</SectionLabel>
                  <h2 className={css.sectionTitle}>{t('AboutPage.contactTitle')}</h2>
                  <p>{t('AboutPage.contactDescription')}</p>
                </div>

                <address className={css.contactDetails}>
                  <div className={css.contactDetailItem}>
                    <span className={css.contactIcon}>
                      <MapPin aria-hidden="true" size={18} strokeWidth={2} />
                    </span>
                    <div>
                      <strong>{t('AboutPage.companyName')}</strong>
                      <p>{t('AboutPage.companyAddress')}</p>
                    </div>
                  </div>
                  <div className={css.contactDetailItem}>
                    <span className={css.contactIcon}>
                      <Mail aria-hidden="true" size={18} strokeWidth={2} />
                    </span>
                    <div>
                      <strong>{t('AboutPage.emailLabel')}</strong>
                      <a href="mailto:hello@sceneaccess.co">hello@sceneaccess.co</a>
                    </div>
                  </div>
                  <a className={css.primaryCta} href="mailto:hello@sceneaccess.co">
                    {t('AboutPage.getInTouch')}
                    <ArrowRight aria-hidden="true" size={18} strokeWidth={1.9} />
                  </a>
                </address>

                <div className={css.lineCard}>
                  <div className={css.lineHeader}>
                    <h3>{t('AboutPage.lineTitle')}</h3>
                    <p>{t('AboutPage.lineDescription')}</p>
                  </div>

                  {lineQrImage ? (
                    <Zoom>
                      <img
                        className={css.lineQr}
                        src={lineQrImage}
                        alt={t('AboutPage.lineQrAlt')}
                        loading="lazy"
                      />
                    </Zoom>
                  ) : (
                    <div
                      className={css.lineQrPlaceholder}
                      role="img"
                      aria-label={t('AboutPage.lineQrPlaceholderLabel')}
                    >
                      {t('AboutPage.lineQrPlaceholderText')}
                    </div>
                  )}

                  <a
                    className={css.secondaryCta}
                    href={lineOfficialAccountUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img className={css.lineButtonIcon} src={lineLogo} alt="" loading="lazy" />
                    {t('AboutPage.addOnLine')}
                    <ArrowRight aria-hidden="true" size={16} strokeWidth={1.9} />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const AboutPage = compose(connect(mapStateToProps))(AboutPageComponent);

export { AboutPageComponent };

export default AboutPage;
